import { ErrorOutlineRounded } from "@mui/icons-material";
import { Card, CardHeader, CardContent, Typography, Box } from "@mui/material";
import { ReactNode, ReactElement } from "react";

import {
  StartCampaignMutation,
  StartCampaignMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import Redirect from "~frontend/components/Redirect";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import { START_CAMPAIGN } from "~frontend/graphql/campaigns";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { jobFailed, jobInProgress } from "~frontend/util/jobs";
import useMutation from "~frontend/util/useMutation";

import { StepInstructions } from "./StepInstructions";
import { CampaignForEditor } from "./types";

export type ValidationError = {
  title: string;
  message: ReactNode;
  pageName: string;
  pageLink: string;
};

function ValidationErrorCard({
  validationErrors,
}: {
  validationErrors: ValidationError[];
}): ReactElement {
  return (
    <Card sx={{ backgroundColor: "transparent" }}>
      <CardHeader title="Errors" />
      <CardContent>
        <FlexBox direction="column" gap={3}>
          {validationErrors.map(error => (
            <FlexBox key={error.title}>
              <Box sx={{ paddingLeft: 1, paddingRight: 1 }}>
                <ErrorOutlineRounded color="error" />
              </Box>
              <Box>
                <Typography
                  variant="body1Bold"
                  sx={{ color: palette.red, marginBottom: 1 }}
                >
                  {error.title}
                </Typography>
                <Typography variant="body1">
                  {error.message}
                  <br />
                  <InternalLink to={error.pageLink}>
                    Go to the {error.pageName} step to correct
                  </InternalLink>
                </Typography>
              </Box>
            </FlexBox>
          ))}
        </FlexBox>
      </CardContent>
    </Card>
  );
}

function JobRunning({
  job,
}: {
  job: NonNullable<CampaignForEditor["campaignStartJob"]>;
}): ReactElement {
  return (
    <Loading
      progress={job.progress}
      message={
        <>
          <span>Starting Campaign ({job.progress.toFixed(1)}%)...</span>
          <br />
          <em>You can leave and the process will continue in the background</em>
        </>
      }
    />
  );
}

export default function StartCampaign({
  campaign,
  validationErrors,
}: {
  campaign: CampaignForEditor;
  validationErrors: ValidationError[];
}): ReactElement {
  const { subaccountLink } = useSubaccount()!;

  const [startCampaign, { loading: isSaving }] = useMutation<
    StartCampaignMutation,
    StartCampaignMutationVariables
  >(START_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not start campaign",
    variables: {
      input: {
        campaignId: campaign.id,
      },
    },
    awaitRefetchQueries: true,
    refetchQueries: ["getCampaign"],
  });

  if (campaign.startedAt) {
    return <Redirect to={subaccountLink(`/campaigns/${campaign.shortId}`)} />;
  }

  if (campaign.status === "ARCHIVED") {
    return (
      <Redirect
        to={subaccountLink(`/campaigns/${campaign.shortId}/edit/basics`)}
      />
    );
  }

  if (campaign.campaignStartJob && jobInProgress(campaign.campaignStartJob)) {
    return <JobRunning job={campaign.campaignStartJob} />;
  }

  function renderContent() {
    if (validationErrors.length) {
      return (
        <>
          <Typography variant="h2" sx={{ marginBottom: 4 }}>
            We found some errors with your campaign. Please correct these, then
            return to this page to start your campaign.
          </Typography>
          <ValidationErrorCard validationErrors={validationErrors} />
        </>
      );
    }

    if (
      campaign.campaignStartJob &&
      jobFailed(campaign.campaignStartJob).failed
    ) {
      return (
        <>
          <Typography sx={{ marginBottom: 4 }}>
            There was a problem starting your campaign. Please correct any
            problems or try again later.
          </Typography>
          <Card>
            <CardHeader title="Campaign Failed To Start" />
            <CardContent>
              <Typography variant="body2">
                {jobFailed(campaign.campaignStartJob).errorMessage}
              </Typography>
            </CardContent>
          </Card>
        </>
      );
    }

    return (
      <Typography>
        Your campaign is ready to go! Click the Start Campaign button to
        finalize your campaign and start sending texts. You won't be able to
        change contacts or sending numbers after your campaign has started, but
        you can change your initial message, response scripts, and tags.
      </Typography>
    );
  }

  return (
    <>
      <Box>
        <StepInstructions header="Start This Campaign" sx={{ pb: 0 }} />
      </Box>

      <Box>{renderContent()}</Box>

      <FlexBox justifyContent="flex-end" sx={{ mt: 4 }}>
        <SubmitButton
          variant="contained"
          color="primary"
          isSubmitting={isSaving}
          submitText="Start Campaign"
          loadingText="Starting Campaign..."
          onClick={() => startCampaign()}
          disabled={validationErrors.length > 0}
        />
      </FlexBox>
    </>
  );
}
